// caret-map.js — rendered-markdown-offset → raw-markdown-offset mapper (C1469).
//
// Pure string math, no DOM — same node-testable-leaf convention as drag-order.js /
// task-group-label.js. Used by components/task-detail.js's placeCaretFromClick() to turn
// a click position inside the rendered `.task-detail-description` div into an approximate
// offset inside the raw markdown source, so the edit textarea's caret lands near the word
// the user clicked instead of always at the end of the text.
//
// Why "approximate": renderMarkdown() strips markup (`**bold**` -> `bold`), so there is no
// exact 1:1 index map between rendered text and raw source. Instead we anchor on a "probe"
// — the run of text immediately before the click in the rendered string — and locate that
// same probe in the raw string. A long probe pins the match precisely; if it fails (spans a
// markup boundary, e.g. probe = "see bold" vs raw "see **bold**") we retry with a shorter
// probe until one matches, or give up and return null (caller falls back to end-of-text).

// Escapes regex-special chars in `s`, keeping runs of whitespace as `\s+` so a probe that
// crosses a soft-wrapped raw newline (rendered space, raw "\n") still matches.
function toWhitespaceTolerantPattern(s) {
  const escaped = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return escaped.replace(/\s+/g, '\\s+');
}

// Count non-overlapping-safe (allows overlap) occurrences of `probe` (as whitespace-
// tolerant pattern) within `text.slice(0, endIndex)` — used to pick the *matching*
// occurrence in rawText when the probe repeats (e.g. a word used twice).
function occurrencesBefore(text, endIndex, pattern) {
  const re = new RegExp(pattern, 'g');
  let count = 0;
  let m;
  const hay = text.slice(0, endIndex);
  while ((m = re.exec(hay))) {
    count++;
    if (m.index === re.lastIndex) re.lastIndex++; // guard zero-width match
  }
  return count;
}

// Returns the start index (in `text`) of the Nth (0-based) occurrence of `pattern`, or -1.
function nthOccurrenceIndex(text, pattern, n) {
  const re = new RegExp(pattern, 'g');
  let count = 0;
  let m;
  let last = -1;
  while ((m = re.exec(text))) {
    last = m.index;
    if (count === n) return { index: m.index, length: m[0].length };
    count++;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  // Fewer occurrences in raw than in rendered — fall back to the last one found.
  return last >= 0 ? { index: last, length: 0 } : null;
}

const PROBE_LENGTHS = [32, 24, 16, 12, 8, 6, 4, 3];

/**
 * @param {string} renderedText - flattened text content of the rendered markdown DOM
 * @param {number} renderedIndex - click offset within renderedText
 * @param {string} rawText - raw markdown source (task.description)
 * @returns {number|null} approximate offset within rawText, or null if no probe matched
 */
export function rawOffsetFromRendered(renderedText, renderedIndex, rawText) {
  if (!rawText) return null;
  if (renderedIndex <= 0) return 0;
  if (!renderedText) return null;

  const clampedIndex = Math.min(renderedIndex, renderedText.length);

  for (const len of PROBE_LENGTHS) {
    const start = Math.max(0, clampedIndex - len);
    const probe = renderedText.slice(start, clampedIndex);
    if (!probe.trim()) continue; // whitespace-only probe — too weak to anchor on

    const pattern = toWhitespaceTolerantPattern(probe);
    let occurrence;
    try {
      occurrence = occurrencesBefore(renderedText, clampedIndex, pattern) - 1; // 0-based index of *this* probe
    } catch {
      continue; // malformed pattern (shouldn't happen given escaping) — try shorter probe
    }
    if (occurrence < 0) continue;

    let hit;
    try {
      hit = nthOccurrenceIndex(rawText, pattern, occurrence);
    } catch {
      continue;
    }
    if (!hit) continue;

    return Math.min(hit.index + (hit.length || probe.length), rawText.length);
  }

  return null;
}
